import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { RecoveryCodesResponse, TotpSetupResponse } from '@cloudsign/shared';
import { api } from '../lib/api';
import { t } from '../lib/i18n';
import { ErrorLine } from './ErrorPanel';
import { Button, Spinner, TextField } from './ui';

export function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">{t('auth.recoveryCodesTitle')}</h2>
      <p className="text-sm">{t('auth.recoveryCodesPrompt')}</p>
      <ul className="grid grid-cols-2 gap-2 rounded-md bg-slate-100 p-3 font-mono text-sm" aria-label={t('auth.recoveryCodesTitle')}>
        {codes.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
      <Button variant="primary" onClick={onDone}>
        {t('auth.recoveryCodesSaved')}
      </Button>
    </div>
  );
}

/**
 * Two-factor enrolment: shows a QR code, checks the first code, then shows the recovery codes once.
 * Used at login (when 2FA is required) and from the account page.
 */
export function TotpEnrol({ onDone, onCancel }: { onDone: () => void; onCancel?: () => void }) {
  const setup = useQuery({
    queryKey: ['totp-setup'],
    queryFn: () => api.post('/auth/totp/setup', { schema: TotpSetupResponse }),
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const [qr, setQr] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const url = setup.data?.otpauthUrl;

  useEffect(() => {
    let cancelled = false;
    if (url) {
      QRCode.toDataURL(url, { margin: 1, width: 192 })
        .then((d) => {
          if (!cancelled) setQr(d);
        })
        .catch(() => {
          if (!cancelled) setQr(null);
        });
    }
    return () => {
      cancelled = true;
    };
  }, [url]);

  const enable = useMutation({
    mutationFn: () => api.post('/auth/totp/enable', { body: { code }, schema: RecoveryCodesResponse }),
    onSuccess: (r) => setCodes(r.codes),
  });

  if (codes) return <RecoveryCodes codes={codes} onDone={onDone} />;
  if (setup.isPending) return <Spinner />;
  if (setup.isError) return <ErrorLine error={setup.error} />;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    enable.mutate();
  };
  return (
    <form onSubmit={submit} className="space-y-4">
      <p className="text-sm">{t('auth.totpSetupPrompt')}</p>
      {qr ? <img src={qr} width={192} height={192} alt={t('auth.qrAlt')} className="mx-auto" /> : null}
      <p className="break-all text-center font-mono text-sm">{t('auth.totpManualKey', { secret: setup.data.secret })}</p>
      <ErrorLine error={enable.error} />
      <TextField
        label={t('auth.totpCode')}
        inputMode="numeric"
        autoComplete="one-time-code"
        required
        value={code}
        onChange={(e) => setCode(e.target.value)}
      />
      <div className="flex gap-2">
        <Button type="submit" variant="primary" disabled={enable.isPending}>
          {t('auth.turnOn2fa')}
        </Button>
        {onCancel ? <Button onClick={onCancel}>{t('common.cancel')}</Button> : null}
      </div>
    </form>
  );
}
